import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { detectDevice, scrollToTop, setBodyOverflow } from 'src/helpers/utils';
import { gameListRequest } from 'src/services/gameApi';
import { openMyAccountModal } from 'src/store/account-modal/actions';
import { setGameBoxWidgetOpen, setSelectedCategory } from 'src/store/app/actions';
import { openAuthModal } from 'src/store/auth-modal/actions';
import {
  setGamesTotalCount,
  setSelectedGame,
  setSelectedProvider,
  setSelectedTag,
  setTags,
} from 'src/store/games/actions';
import { RootState } from 'src/types/store-types';
import { useConnectedAction } from './use-connected-action';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useBannerActions = (): any => {
  const device = detectDevice();
  const navigate = useNavigate();

  const _setSelectedTag = useConnectedAction(setSelectedTag);
  const _setTags = useConnectedAction(setTags);
  const _setSelectedProvider = useConnectedAction(setSelectedProvider);
  const _setGamesTotalCount = useConnectedAction(setGamesTotalCount);
  const _openAuthModal = useConnectedAction(openAuthModal);
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  const _setSelectedGame = useConnectedAction(setSelectedGame);
  const _openAccountModal = useConnectedAction(openMyAccountModal);
  const _setGameBoxWidgetOpen = useConnectedAction(setGameBoxWidgetOpen);

  const { selectedCategory } = useSelector((state: RootState) => state.app);
  const { user } = useSelector((state: RootState) => state.user);
  const { selectedTag } = useSelector((state: RootState) => state.games);
  const { generalConfigs, navigation } = useSelector((state: RootState) => state.configs);

  const gamesCount =
    device === 'mobile' ? generalConfigs?.gamesCountPerPageMobile : generalConfigs?.gamesCountPerPageDesktop;

  const getGameList = (provider: string, offset: number, tagList: []): void => {
    gameListRequest(device, [provider], offset, selectedCategory, [selectedTag], gamesCount).then((res) => {
      const newGames = res.games.map((game: any) => {
        return game;
      });
      _setGamesTotalCount(res.totalCount);
      _setTags(provider === 'all_providers' ? tagList : newGames);
    });
  };

  const onCallProviderAction = (params: { provider: string; tags: []; tagList: [] }): void => {
    const { provider, tags, tagList } = params;
    const tagsData = tags || [];
    _setSelectedProvider(provider);
    selectedTag === 'home' && _setSelectedTag('allGames');
    const offset = Math.floor(tagsData.length / 24) <= 1 ? 0 : Math.floor(tagsData.length / 24);
    getGameList(provider, offset, tagList);
    _setSelectedCategory('casino');
    navigate(`/category/casino`);
  };

  const onCallLoginAction = (): void => {
    if (!user) {
      setBodyOverflow('unset');
      _openAuthModal('login');
    }
  };

  const onCallRegistrAction = (): void => {
    if (!user) {
      _setSelectedCategory('');
      setBodyOverflow('set');
      scrollToTop();
      navigate('/signup');
    }
  };

  const onCallDepositAction = (): void => {
    if (user) {
      _openAccountModal({ section: 'deposit', subSection: 'deposit' });
    } else {
      onCallLoginAction();
    }
  };

  const onCallRefFriendAction = (): void => {
    if (user) {
      _openAccountModal({ section: 'referAFriend', subSection: 'referAFriend' });
    } else {
      onCallLoginAction();
    }
  };

  const onCallGameBoxAction = (): void => {
    _setSelectedCategory('');
    if (user) {
      _setGameBoxWidgetOpen();
    } else {
      onCallLoginAction();
    }
  };

  const onCallPromotionAction = (promotionId: string | number): void => {
    _setSelectedCategory('');
    navigate(`/promotions/${promotionId}`);
  };

  const onCallExternalUrlAction = (url: string): void => {
    window.open(url, '_blank', 'noreferrer');
  };

  const onCallGame = (gameId: string): void => {
    if (user) {
      _setSelectedGame({ id: gameId, gameWalletId: null });
    } else {
      setBodyOverflow('unset');
      _openAuthModal('login');
    }
  };

  const onCallCategoryAction = (category: string): void => {
    const routeExisting = navigation.some((f) => f.name === category || f.slug === category);

    if (routeExisting) {
      if (category === 'poker') {
        navigate(`/content/${category}`);
      } else {
        navigate(`/category/${category}`);
        _setSelectedCategory(category);
      }
    } else {
      console.error('Category doesn"t exist');
    }
  };

  return {
    providerAction: (params: any) => onCallProviderAction(params),
    loginAction: () => onCallLoginAction(),
    registrAction: () => onCallRegistrAction(),
    depositAction: () => onCallDepositAction(),
    gameBoxAction: () => onCallGameBoxAction(),
    refFriendAction: () => onCallRefFriendAction(),
    promotionAction: (promotionId: string | number) => onCallPromotionAction(promotionId),
    externalUrlAction: (url: string) => onCallExternalUrlAction(url),
    gameAction: (gameId: string) => onCallGame(gameId),
    categoryAction: (category: string) => onCallCategoryAction(category),
  };
};
